import React, {
    SetStateAction,
    Dispatch,
    useEffect,
    useMemo,
    useRef,
} from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box } from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialog, PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
// eslint-disable-next-line import/no-unresolved
import { Notify } from 'notify/services/Notify';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import { GetCardControlsResponse } from '@clients/domains/flexControls/flexControlAccountClient';
import { AccountFlexControlsFormInputs } from '@clients/domains/flexControls/flexControlsClient';
import { useAccount } from '@contexts/AccountProvider';
import usePatchUpdateAccountFlexControl from '@hooks/queries/usePatchUpdateAccountFlexControl';
import {
    attributes,
    operators,
} from '@pages/flexControls/createFlexControl/fieldParameters';
import BasicDetailsSharedForm from '@pages/flexControls/shared/BasicDetailsForm';
import BasicDetailsSchema from '@pages/flexControls/shared/BasicDetailsSchema';
import ConditionsShared from '@pages/flexControls/shared/ConditionsForm';
import ConditionsSchema from '@pages/flexControls/shared/ConditionsSchema';
import ResetPeriodShared from '@pages/flexControls/shared/ResetPeriodForm';
import ResetPeriodSchema from '@pages/flexControls/shared/ResetPeriodSchema';
import { formatAccountFlexControlInputs } from '@utils/formatFlexControlInputs';
import getEnumTranslation from '@utils/getEnumTranslation';
import { ResetPeriod } from '@utils/resetPeriod';

interface CardControlEditModalProps {
    cardControlItem?: GetCardControlsResponse;
    accountName: string;
    showEditModal: boolean;
    setShowEditModal: Dispatch<SetStateAction<boolean>>;
}

const notify = Notify.getInstance();

const completeValidationSchema = yup.object({
    ...BasicDetailsSchema,
    ...ResetPeriodSchema,
    ...ConditionsSchema,
});

const AccountControlEditModal = ({
    cardControlItem,
    accountName,
    showEditModal,
    setShowEditModal,
}: CardControlEditModalProps) => {
    const { account } = useAccount();
    const { formatMessage } = useIntl();
    const errorScroll = useRef(null);

    const passedValues = useMemo(() => {
        return {
            name: cardControlItem?.name,
            type: cardControlItem?.type,
            beneficiary_id: cardControlItem?.beneficiary_id,
            processing_codes: cardControlItem?.processing_codes?.join(','),
            override_controls: cardControlItem?.override_controls?.join(','),
            currency_code: cardControlItem?.currency_code,
            time_zone: cardControlItem?.time_zone,
            deny_code: cardControlItem?.deny_code,
            max_limit: cardControlItem?.max_limit?.toString(),
            limit_duration: ResetPeriod(cardControlItem?.limit_duration),
            conditions: cardControlItem?.conditions,
            week_day: dayjs(cardControlItem?.reset_datetime)
                .format('dddd')
                .toLocaleLowerCase(),
            month_day: dayjs(cardControlItem?.reset_datetime).date().toString(),
            time: dayjs(cardControlItem?.reset_datetime).format('HH:mm'),
        } as AccountFlexControlsFormInputs;
    }, [cardControlItem]);

    const methods = useForm({
        shouldUnregister: false,
        resolver: yupResolver(completeValidationSchema),
        mode: 'onSubmit',
        defaultValues: passedValues,
    });
    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = methods;

    // when the default values change reset the form default values.
    useEffect(() => reset(passedValues), [passedValues, reset]);

    const { mutate, error, isError } = usePatchUpdateAccountFlexControl({
        accountId: account?.account_id,
        flexControlId: cardControlItem?.id,
        onSuccess: () => {
            notify.addNotification(
                formatMessage(
                    {
                        id: 'AccountControlEditModal.success',
                        defaultMessage:
                            'You’ve successfully updated this account control {flexControlName}.',
                    },
                    {
                        flexControlName: cardControlItem?.name,
                    },
                ),
            );
            setShowEditModal(false);
        },
        onError: () => {
            errorScroll.current.scrollIntoView();
        },
    });

    const onSubmit = (data: AccountFlexControlsFormInputs) => {
        // remove Type cannot be patched so will get an API error
        delete data.type;
        const dataToMutate = formatAccountFlexControlInputs(data);
        mutate(dataToMutate);
    };

    const errorMessage = (
        <FormattedMessage
            id="CardControlEditModal.errorMessage"
            defaultMessage="{message} in form field {field}"
            values={{
                message: (error as AxiosError)?.response?.data?.details[0]
                    ?.message,
                field: (
                    error as AxiosError
                )?.response?.data?.details[0]?.location.replace('payload.', ''),
            }}
        />
    );

    return (
        <FormProvider {...methods}>
            <PDialog
                open={showEditModal}
                dialogTitle={
                    <FormattedMessage
                        id="CardControlEditModal.title"
                        defaultMessage="Edit {name}'s account control"
                        values={{
                            name: accountName,
                        }}
                    />
                }
                dialogSubtitle={
                    <FormattedMessage
                        id="CardControlEditModal.subtitle"
                        defaultMessage="You can edit this control by filling out the input fields below then clicking save."
                    />
                }
                onClose={() => setShowEditModal(false)}>
                <form
                    data-testid="flex-account-edit"
                    onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ mt: -2 }} ref={errorScroll} />
                    <PDialogContent>
                        {isError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}

                        <BasicDetailsSharedForm
                            isEditing
                            hasTimeZone={passedValues.time_zone?.length > 0}
                        />

                        {cardControlItem?.type !== 'restriction' && (
                            <ResetPeriodShared isEditing />
                        )}

                        <ConditionsShared
                            attributes={attributes}
                            operators={operators}
                            isEditing
                        />
                    </PDialogContent>
                    <PDialogAction>
                        <Button
                            onClick={() => {
                                setShowEditModal(false);
                            }}>
                            {getEnumTranslation('cancel')}
                        </Button>
                        <Button
                            disabled={!isDirty}
                            type="submit"
                            variant="contained">
                            {getEnumTranslation('save')}
                        </Button>
                    </PDialogAction>
                </form>
            </PDialog>
        </FormProvider>
    );
};

export default AccountControlEditModal;
